import type { SlimeState } from './slime-state.js';

export type MaterialType = 'organic' | 'mineral' | 'essence' | 'synthetic';

export interface IngestedItem {
  id: string;
  name: string;
  materialType: MaterialType;
  massKg: number;
  gelEnergyYield: number;
  biomassYield: number;
  essenceType?: string;
  dissolutionTimeSec: number;
  elapsedSec: number;
}

export interface IngestionInput {
  id: string;
  name: string;
  materialType: MaterialType;
  massKg: number;
  gelEnergyYield: number;
  biomassYield: number;
  essenceType?: string;
  customDurationSec?: number;
}

export interface DissolutionResult {
  item: IngestedItem;
  gainedMassKg: number;
  restoredEnergy: number;
  gainedBiomass: number;
  essenceType?: string;
}

export class DissolutionEngine {
  private queue: IngestedItem[] = [];
  public digestionRateMultiplier: number = 1.0;

  public static getBaseRateForMaterial(material: MaterialType): number {
    switch (material) {
      case 'organic':
        return 2.0; // 2 seconds per item
      case 'mineral':
        return 6.0; // 6 seconds per item
      case 'essence':
        return 3.0; // 3 seconds per item
      case 'synthetic':
        return 8.0; // 8 seconds per item
    }
  }

  public ingest(input: IngestionInput): boolean {
    const baseDuration = input.customDurationSec ?? DissolutionEngine.getBaseRateForMaterial(input.materialType);
    const item: IngestedItem = {
      ...input,
      dissolutionTimeSec: baseDuration,
      elapsedSec: 0
    };
    this.queue.push(item);
    return true;
  }

  public getQueue(): readonly IngestedItem[] {
    return this.queue;
  }

  public getQueueLength(): number {
    return this.queue.length;
  }

  public update(dt: number, slimeState: SlimeState): DissolutionResult[] {
    const completed: DissolutionResult[] = [];
    const remaining: IngestedItem[] = [];

    for (const item of this.queue) {
      item.elapsedSec += dt * this.digestionRateMultiplier;
      if (item.elapsedSec >= item.dissolutionTimeSec) {
        slimeState.addMass(item.massKg);
        slimeState.restoreEnergy(item.gelEnergyYield);
        completed.push({
          item,
          gainedMassKg: item.massKg,
          restoredEnergy: item.gelEnergyYield,
          gainedBiomass: item.biomassYield,
          essenceType: item.essenceType
        });
      } else {
        remaining.push(item);
      }
    }

    this.queue = remaining;
    return completed;
  }
}
